import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { BreadcrumbListLD, ArticleLD, WebPageLD } from '@/components/base/SEO';
import { getArticleById, allArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 桔子视频资讯`;
    } else {
      document.title = '文章未找到 - 桔子视频';
    }
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-3xl text-foreground-500"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">您访问的文章可能已被删除或不存在</p>
            <Link to="/" className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const breadcrumbItems = [
    { name: '首页', url: 'https://juzivideo.com/' },
    { name: article.category, url: 'https://juzivideo.com/category/all' },
    { name: article.title, url: `https://juzivideo.com/article/${article.id}` },
  ];

  const relatedArticles = allArticles.filter((a) => a.id !== article.id).slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <BreadcrumbListLD items={breadcrumbItems} />
      <ArticleLD
        article={{
          title: article.title,
          description: article.excerpt,
          url: `https://juzivideo.com/article/${article.id}`,
          datePublished: article.date,
          author: article.author,
          image: article.coverImage,
        }}
      />
      <WebPageLD name={article.title} description={article.excerpt} url={`https://juzivideo.com/article/${article.id}`} />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          {/* Breadcrumb */}
          <nav className="w-full px-4 md:px-6 py-4" aria-label="面包屑导航">
            <div className="max-w-3xl mx-auto flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
              <Link to="/" className="hover:text-primary-400 transition-colors whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/category/all" className="hover:text-primary-400 transition-colors whitespace-nowrap">全部视频</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-700 line-clamp-1">{article.title}</span>
            </div>
          </nav>

          <article className="w-full px-4 md:px-6 pb-12">
            <div className="max-w-3xl mx-auto">
              {/* Header */}
              <header className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-2 py-0.5 rounded text-xs bg-accent-500/15 text-accent-400">
                    {article.category}
                  </span>
                  <span className="text-xs text-foreground-500">{article.date}</span>
                  <span className="text-xs text-foreground-500">· {article.readTime}阅读</span>
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                  {article.title}
                </h1>
                <div className="flex items-center gap-2 text-sm text-foreground-500">
                  <i className="ri-user-line"></i>
                  <span>{article.author}</span>
                </div>
              </header>

              {/* Cover Image */}
              <div className="aspect-video rounded-lg overflow-hidden bg-background-200 mb-8">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  title={`${article.title} - 桔子视频资讯`}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Article Content */}
              <div
                className="prose prose-invert max-w-none text-sm text-foreground-700 leading-relaxed article-content"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {/* Tags */}
              <div className="mt-8 pt-6 border-t border-background-200">
                <div className="flex flex-wrap items-center gap-2">
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full text-xs bg-background-100 text-foreground-600 border border-background-200">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </article>

          {/* Related Articles */}
          <section className="w-full px-4 md:px-6 py-10 bg-background-100/50">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-lg font-bold text-foreground-950 mb-5" style={{ fontFamily: 'var(--font-heading)' }}>
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group block bg-background-100 rounded-lg border border-background-200 overflow-hidden hover:border-primary-500/30 transition-all duration-200 cursor-pointer"
                  >
                    <div className="aspect-video overflow-hidden bg-background-200">
                      <img
                        src={ra.coverImage}
                        alt={ra.title}
                        title={`${ra.title} - 桔子视频资讯`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-0.5 rounded text-xs bg-accent-500/15 text-accent-400">
                          {ra.category}
                        </span>
                        <span className="text-xs text-foreground-500">{ra.date}</span>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-400 transition-colors line-clamp-2">
                        {ra.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}